import { FC, createContext, useContext, useCallback, useEffect } from 'react';

import {
  DataPaginateResponse,
  PaginationRequestParams,
  ErrorResponse,
} from '~globals/types/api';
import { Nullable } from '~globals/types/commons';
import { PlanFeaturesTypes } from '~globals/types/enums';
import {
  useParseFiltersRequestSearchItems,
  useSelectionIds,
  useToggle,
  useDataTableRequest,
  useToggleDialogConfirmation,
  useSubscriptionFeatures,
} from '~hooks/index';
import { ItemsSearchFilters } from '~hooks/useItemsSearchFilters';
import { searchItemsV2 } from '~services/item';
import {
  ItemFilterV2Base,
  ItemExtended,
  ItemsSearchHead,
} from '~services/item/types';
import { getLimitPremiunByVersion } from '~utils/feature';

import { useDashboardVariantV2CarrierContext } from '../../../Context';
// eslint-disable-next-line max-len
import { useDashboardVariantV2CarrierInTransitFiltersContext } from '../../../InTransitFiltersContext';

import {
  DashboardVariantV2CarrierContentInTransitTabContextValue,
  DashboardVariantV2CarrierContentInTransitTabDialogDetailsOrderData,
  DashboardVariantV2CarrierContentInTransitTabDialogChangeStateOrderData,
} from './types';
import {
  DEFAULT_PAGINATION,
  DEFAULT_FILTERS_PAYLOAD,
  DEFAULT_ROUTE_ITEM_STATES,
} from './utils';

const DashboardVariantV2CarrierContentInTransitTabContext =
  createContext<DashboardVariantV2CarrierContentInTransitTabContextValue>({
    parseFiltersRequest: () => DEFAULT_FILTERS_PAYLOAD,
    selection: {
      selectedIds: [],
      utils: {
        add: () => {},
        addMultiple: () => {},
        toggle: () => {},
        deleteOne: () => {},
        deleteMultiple: () => {},
        deleteCurrentAndAfter: () => {},
        has: () => false,
        isEmpty: () => true,
        reset: () => {},
      },
    },
    table: {
      data: [],
      stats: null,
      loading: true,
      error: null,
      reloadRequest: () => {},
      totalSize: 0,
      queryParams: {
        pagination: DEFAULT_PAGINATION,
        filters: DEFAULT_FILTERS_PAYLOAD,
      },
      handleInitialData: () => {},
      handleChangeTable: () => {},
      handleInitialTable: () => {},
    },
    showTracking: {
      value: false,
      toggleValue: () => {},
    },
    isOnlyTodayDelivery: {
      value: false,
      toggleValue: () => {},
    },
    actions: {
      reloadDataAndResetSelectedRows: () => {},
      handleForceRequests: () => {},
    },
    dialogs: {
      detailsOrder: {
        data: null,
        open: false,
        handleOpen: () => {},
        handleClose: () => {},
      },
      changeStateOrder: {
        data: null,
        open: false,
        handleOpen: () => {},
        handleClose: () => {},
      },
      modifyAssignmentOrder: {
        data: null,
        open: false,
        handleOpen: () => {},
        handleClose: () => {},
      },
      setFeedbackStop: {
        data: null,
        open: false,
        handleOpen: () => {},
        handleClose: () => {},
      },
    },
  });

export const useDashboardVariantV2CarrierContentInTransitTabContext =
  (): DashboardVariantV2CarrierContentInTransitTabContextValue =>
    useContext(DashboardVariantV2CarrierContentInTransitTabContext);

const DashboardVariantV2CarrierContentInTransitTabProvider: FC = ({
  children,
}) => {
  const { hasAccessToFeature } = useSubscriptionFeatures();

  const {
    activeTab: { currentTab },
  } = useDashboardVariantV2CarrierContext();
  const { filters, forceRequest } =
    useDashboardVariantV2CarrierInTransitFiltersContext();

  const parseFiltersRequest = useParseFiltersRequestSearchItems({
    filters: filters.filters,
    activeFilterTabId: currentTab,
    defaultRouteItemState: DEFAULT_ROUTE_ITEM_STATES,
    defaultCollectItemState: [],
  });

  const [showTracking, toggleShowTracking] = useToggle(
    hasAccessToFeature(
      PlanFeaturesTypes.LimitPremium,
      getLimitPremiunByVersion('1.22.3'),
    ),
  );
  const [isOnlyTodayDelivery, toggleIsOnlyTodayDelivery] = useToggle(false);

  const selection = useSelectionIds<string>([]);

  const table = useDataTableRequest<
    PaginationRequestParams<ItemFilterV2Base>,
    DataPaginateResponse<ItemExtended[], null, ItemsSearchHead>,
    ErrorResponse
  >({
    request: searchItemsV2,
    payload: {
      pagination: DEFAULT_PAGINATION,
      filters: parseFiltersRequest(),
    },
    isValidToRequest: !showTracking,
  });

  const handleForceRequests = useCallback(
    (currentFilters?: Partial<ItemsSearchFilters>) => {
      selection.utils.reset();

      table.handleChangeTable('filter', {
        pagination: DEFAULT_PAGINATION,
        filters: parseFiltersRequest(currentFilters),
      });

      forceRequest.toggleValue(false);
    },
    [forceRequest, parseFiltersRequest, selection.utils, table],
  );

  useEffect(() => {
    if (forceRequest.value) {
      toggleShowTracking(false);
      handleForceRequests();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [forceRequest.value]);

  const reloadDataAndResetSelectedRows = useCallback(() => {
    selection.utils.reset();

    if (table.queryParams.pagination.page === DEFAULT_PAGINATION.page) {
      table.reloadRequest();
    } else {
      table.handleChangeTable('filter', {
        pagination: table.queryParams.pagination,
        filters: parseFiltersRequest(),
      });
    }
  }, [selection.utils, table, parseFiltersRequest]);

  const [
    openDialogDetailsOrder,
    selectedForDetailsOrder,
    handleOpenDialogDetailsOrder,
    handleCloseDialogDetailsOrder,
  ] =
    useToggleDialogConfirmation<
      Nullable<DashboardVariantV2CarrierContentInTransitTabDialogDetailsOrderData>
    >(null);

  const [
    openDialogChangeStateOrder,
    selectedOrderDataToChangeState,
    handleOpenDialogChangeStateOrder,
    handleCloseDialogChangeStateOrder,
  ] =
    useToggleDialogConfirmation<
      Nullable<DashboardVariantV2CarrierContentInTransitTabDialogChangeStateOrderData>
    >(null);

  const [
    openDialogModifyAssignmentOrder,
    selectedItemIdToModifyAssignment,
    handleOpenDialogModifyAssignmentOrder,
    handleCloseDialogModifyAssignmentOrder,
  ] = useToggleDialogConfirmation<Nullable<string>>(null);

  const [
    openDialogSetFeedbackStop,
    selectedItemSetFeedbackStop,
    handleOpenDialogSetFeedbackStop,
    handleCloseDialogSetFeedbackStop,
  ] = useToggleDialogConfirmation<Nullable<ItemExtended>>(null);

  return (
    <DashboardVariantV2CarrierContentInTransitTabContext.Provider
      value={{
        parseFiltersRequest,
        selection,
        table,
        showTracking: {
          value: showTracking,
          toggleValue: toggleShowTracking,
        },
        isOnlyTodayDelivery: {
          value: isOnlyTodayDelivery,
          toggleValue: toggleIsOnlyTodayDelivery,
        },
        actions: {
          reloadDataAndResetSelectedRows,
          handleForceRequests,
        },
        dialogs: {
          detailsOrder: {
            open: openDialogDetailsOrder,
            data: selectedForDetailsOrder,
            handleOpen: handleOpenDialogDetailsOrder,
            handleClose: handleCloseDialogDetailsOrder,
          },
          changeStateOrder: {
            open: openDialogChangeStateOrder,
            data: selectedOrderDataToChangeState,
            handleOpen: handleOpenDialogChangeStateOrder,
            handleClose: handleCloseDialogChangeStateOrder,
          },
          modifyAssignmentOrder: {
            open: openDialogModifyAssignmentOrder,
            data: selectedItemIdToModifyAssignment,
            handleOpen: handleOpenDialogModifyAssignmentOrder,
            handleClose: handleCloseDialogModifyAssignmentOrder,
          },
          setFeedbackStop: {
            open: openDialogSetFeedbackStop,
            data: selectedItemSetFeedbackStop,
            handleOpen: handleOpenDialogSetFeedbackStop,
            handleClose: handleCloseDialogSetFeedbackStop,
          },
        },
      }}
    >
      {children}
    </DashboardVariantV2CarrierContentInTransitTabContext.Provider>
  );
};

export default DashboardVariantV2CarrierContentInTransitTabProvider;
